import { computed, onScopeDispose, readonly, shallowRef, toValue } from "vue"
import type { MaybeRefOrGetter } from "vue"

import {
  CaptureApiError,
  closeCaptureGroup,
  createCaptureBatch,
  finishCaptureBatch,
  getCaptureBatch,
  getResumableCaptureBatch,
  startNextCaptureGroup,
  uploadCapturePhoto,
} from "@/lib/captureApi"
import {
  CaptureQueueFullError,
  acknowledgePhoto,
  clearFinishedBatch,
  discardPhoto,
  enqueuePhoto,
  listQueuedBatches,
  replaceQueuedBatch,
  restoreBatch,
  saveBatch,
} from "@/lib/captureQueue"
import { errorMessage } from "@/lib/errors"
import type {
  CaptureBatch,
  CaptureGroup,
  CapturePhoto,
  CaptureQueueBatch,
  CaptureQueueSnapshot,
  QueuedCapturePhoto,
} from "@/types/capture"

/** Mirror the server-side ceilings in CapturePhotos::Upload. */
export const MAX_GROUP_PHOTOS = 10
export const MAX_BATCH_PHOTOS = 500

export type BulkCapturePhase =
  | "idle"
  | "loading"
  | "resume"
  | "starting"
  | "capturing"
  | "closing"
  | "blocked"
  | "recovering"
  | "uploading"
  | "finishing"
  | "ready"

export interface BulkCaptureThumbnail {
  id: string
  url: string
  pending: boolean
}

/** A photo the server hard-rejected (HTTP 422); awaits an explicit retry or discard. */
export interface RejectedCapturePhoto {
  clientUploadId: string
  groupId: string
  message: string
}

export interface UseBulkCaptureOptions {
  accountId: string
  containerId: MaybeRefOrGetter<string | null>
}

interface ResumableCapture {
  batch: CaptureBatch
  snapshot: CaptureQueueSnapshot
}

interface AcceptedShutter {
  batch: CaptureQueueBatch
  groupId: string
  capturedAt: Date
  blob: Blob
  sequence: number
}

type ReconciliationOutcome = "handled" | "continue"

type RecoveryIntent =
  | { kind: "initialize" }
  | { kind: "reconcile"; snapshot: CaptureQueueSnapshot; offerResume: boolean }
  | { kind: "persist_created"; serverBatch: CaptureBatch; localBatch: CaptureQueueBatch }
  | {
      kind: "persist_authoritative"
      serverBatch: CaptureBatch
      localBatch: CaptureQueueBatch
      pendingPhotos: QueuedCapturePhoto[]
      offerResume: boolean
    }
  | { kind: "finish_cleanup"; serverBatch: CaptureBatch; batchId: string }
  | { kind: "refresh_finished"; batchId: string }
  | {
      kind: "stale_cleanup"
      snapshot: CaptureQueueSnapshot
      serverBatch: CaptureBatch | null
      target: "continue" | "ready"
    }
  | {
      kind: "stale_queue"
      snapshot: CaptureQueueSnapshot
      serverBatch: CaptureBatch | null
      replacementBatch?: CaptureBatch
    }
  | {
      kind: "enqueue_transition"
      target: "capture" | "next" | "finish"
      shutters: AcceptedShutter[]
    }

export function useBulkCapture(options: UseBulkCaptureOptions) {
  const phase = shallowRef<BulkCapturePhase>("idle")
  const batch = shallowRef<CaptureBatch | null>(null)
  const localBatch = shallowRef<CaptureQueueBatch | null>(null)
  const pendingPhotos = shallowRef<QueuedCapturePhoto[]>([])
  const rejectedQueue = shallowRef<{ photo: QueuedCapturePhoto; message: string }[]>([])
  const resumable = shallowRef<ResumableCapture | null>(null)
  const error = shallowRef<string | null>(null)
  const queueBlocked = shallowRef(false)
  const recoveryIntent = shallowRef<RecoveryIntent | null>(null)
  const acceptedEnqueueCount = shallowRef(0)
  const abortController = new AbortController()
  const objectUrls = new Map<string, string>()
  const inFlightEnqueues = new Set<Promise<boolean>>()
  const failedEnqueues = new Set<AcceptedShutter>()
  const queuedPhotoSequences = new Map<string, number>()
  let enqueueChain: Promise<void> = Promise.resolve()
  let nextShutterSequence = 0
  let nextPromise: Promise<void> | null = null
  let finishPromise: Promise<void> | null = null
  let uploadPromise: Promise<boolean> | null = null
  let startPromise: Promise<void> | null = null
  let recoveryPromise: Promise<void> | null = null
  let staleActionPromise: Promise<void> | null = null

  const activeGroupId = computed(() => localBatch.value?.activeGroupId ?? null)
  const activeGroup = computed(() =>
    batch.value?.groups.find((group) => group.id === activeGroupId.value),
  )
  const activeLocalGroup = computed(() =>
    localBatch.value?.groups.find((group) => group.groupId === activeGroupId.value),
  )
  const activePendingPhotos = computed(() =>
    pendingPhotos.value.filter((photo) => photo.groupId === activeGroupId.value),
  )
  const unsyncedActivePhotos = computed(() => {
    const serverUploadIds = new Set(
      (activeGroup.value?.photos ?? []).map((photo) => photo.client_upload_id),
    )
    for (const id of activeLocalGroup.value?.acknowledgedClientUploadIds ?? []) {
      serverUploadIds.add(id)
    }
    return activePendingPhotos.value.filter((photo) => !serverUploadIds.has(photo.clientUploadId))
  })
  const currentPhotoCount = computed(
    () =>
      Math.max(
        activeGroup.value?.actual_photo_count ?? 0,
        activeLocalGroup.value?.actualPhotoCount ?? 0,
      ) + unsyncedActivePhotos.value.length,
  )
  const pendingCount = computed(() => pendingPhotos.value.length)
  const rejectedPhotos = computed<RejectedCapturePhoto[]>(() =>
    rejectedQueue.value.map(({ photo, message }) => ({
      clientUploadId: photo.clientUploadId,
      groupId: photo.groupId,
      message,
    })),
  )
  const acceptedBatchPhotoCount = computed(() => {
    const serverGroups = new Map(
      batch.value!.groups.map((group) => [group.id, group.actual_photo_count]),
    )
    const localGroups = new Map(
      localBatch.value!.groups.map((group) => [group.groupId, group.actualPhotoCount ?? 0]),
    )
    let count = 0
    for (const groupId of serverGroups.keys()) {
      count += Math.max(serverGroups.get(groupId)!, localGroups.get(groupId)!)
    }

    const acknowledgedUploadIds = new Set(
      batch.value!.groups.flatMap((group) => group.photos.map((photo) => photo.client_upload_id)),
    )
    for (const group of localBatch.value!.groups) {
      for (const id of group.acknowledgedClientUploadIds ?? []) acknowledgedUploadIds.add(id)
    }
    count += pendingPhotos.value.filter(
      (photo) => !acknowledgedUploadIds.has(photo.clientUploadId),
    ).length
    return count + acceptedEnqueueCount.value
  })
  const groupNumber = computed(() => activeGroup.value?.position ?? 1)
  const actionAvailable = computed(
    () =>
      (phase.value === "capturing" || phase.value === "uploading") &&
      batch.value !== null &&
      localBatch.value !== null &&
      activeGroupId.value !== null &&
      localBatch.value.transition === undefined,
  )
  const canCapture = computed(
    () =>
      actionAvailable.value &&
      !queueBlocked.value &&
      currentPhotoCount.value + acceptedEnqueueCount.value < MAX_GROUP_PHOTOS &&
      acceptedBatchPhotoCount.value < MAX_BATCH_PHOTOS,
  )
  const canStartNext = computed(
    () => actionAvailable.value && currentPhotoCount.value + acceptedEnqueueCount.value > 0,
  )
  const canFinish = computed(
    () => actionAvailable.value && batch.value !== null && acceptedBatchPhotoCount.value > 0,
  )
  const canRetryRecovery = computed(
    () =>
      phase.value === "blocked" &&
      recoveryIntent.value !== null &&
      recoveryIntent.value.kind !== "stale_queue",
  )
  const canReplaceStaleQueue = computed(
    () => phase.value === "blocked" && recoveryIntent.value?.kind === "stale_queue",
  )
  const canDiscardStaleQueue = computed(() => canReplaceStaleQueue.value)
  const resumableBatch = computed(() => resumable.value?.snapshot.batch ?? null)
  const resumableContainer = computed(() => resumable.value?.batch.container ?? null)
  const currentThumbnails = computed<BulkCaptureThumbnail[]>(() => {
    const acknowledged = (activeGroup.value?.photos ?? []).map((photo) => ({
      id: photo.id,
      url: photo.large_url,
      pending: false,
    }))
    const queued = unsyncedActivePhotos.value.map((photo) => ({
      id: photo.clientUploadId,
      url: objectUrls.get(photo.clientUploadId) ?? "",
      pending: true,
    }))
    return [...acknowledged, ...queued]
  })
  const suggestions = computed(() =>
    (batch.value?.groups ?? [])
      .filter((group) => group.analysis_status === "succeeded" && group.analysis_data.name)
      .map((group) => ({
        groupId: group.id,
        groupNumber: group.position,
        name: group.analysis_data.name!,
      })),
  )

  /**
   * Restored photos carry no in-memory shutter sequence, so a restored photo
   * that is later quarantined and retried would append instead of returning to
   * its captured spot. Seed each one a sequence in the durable queue order
   * (the order restoreBatch already returns) and keep new captures advancing
   * after them, so retry/fold re-inserts land in the right position.
   */
  function seedRestoredSequences(photos: QueuedCapturePhoto[]): void {
    for (const photo of photos) {
      if (queuedPhotoSequences.has(photo.clientUploadId)) continue
      queuedPhotoSequences.set(photo.clientUploadId, nextShutterSequence++)
    }
  }

  async function initialize(): Promise<void> {
    phase.value = "loading"
    error.value = null
    recoveryIntent.value = null

    let localBatches: CaptureQueueBatch[] = []
    try {
      localBatches = await listQueuedBatches(options.accountId)
    } catch (cause) {
      if (isAbortError(cause)) return
      error.value = errorMessage(cause, "Could not read saved captures on this device")
      recoveryIntent.value = { kind: "initialize" }
      phase.value = "blocked"
      return
    }

    for (const local of localBatches) {
      let snapshot: CaptureQueueSnapshot | null
      try {
        snapshot = await restoreBatch(options.accountId, local.batchId)
      } catch (cause) {
        if (isAbortError(cause)) return
        error.value = errorMessage(cause, "Could not restore the saved capture on this device")
        recoveryIntent.value = { kind: "initialize" }
        phase.value = "blocked"
        return
      }
      if (!snapshot) continue
      seedRestoredSequences(snapshot.pendingPhotos)
      const outcome = await reconcileLocalCapture(snapshot, true)
      if (outcome === "handled") return
    }

    try {
      const serverBatch = await getResumableCaptureBatch(abortController.signal)
      if (!serverBatch) {
        phase.value = "idle"
        return
      }
      await acceptAuthoritativeBatch(serverBatch, { pendingPhotos: [] }, true)
    } catch (cause) {
      if (isAbortError(cause)) return
      error.value = errorMessage(cause, "Could not check for an unfinished batch")
      phase.value = "idle"
    }
  }

  async function reconcileLocalCapture(
    snapshot: CaptureQueueSnapshot,
    offerResume: boolean,
  ): Promise<ReconciliationOutcome> {
    localBatch.value = snapshot.batch
    pendingPhotos.value = snapshot.pendingPhotos
    let serverBatch: CaptureBatch
    try {
      serverBatch = await getCaptureBatch(snapshot.batch.batchId, abortController.signal)
    } catch (cause) {
      if (isAbortError(cause)) throw cause
      if (cause instanceof TypeError) {
        if (snapshot.batch.transition) {
          blockCapture(
            snapshot,
            offlineBatchFrom(snapshot.batch),
            "This item transition is incomplete. Reconnect to recover it; queued photos are safe.",
            { kind: "reconcile", snapshot, offerResume },
          )
        } else {
          resumable.value = { batch: offlineBatchFrom(snapshot.batch), snapshot }
          phase.value = "resume"
        }
        return "handled"
      }
      if (isDefinitiveMissingBatch(cause)) {
        return handleNonResumableBatch(snapshot, null, cause.message)
      }
      blockCapture(
        snapshot,
        offlineBatchFrom(snapshot.batch),
        errorMessage(cause, "Could not verify the saved capture"),
        { kind: "reconcile", snapshot, offerResume },
      )
      return "handled"
    }

    if (serverBatch.status !== "capturing") {
      return handleNonResumableBatch(snapshot, serverBatch)
    }

    return acceptAuthoritativeBatch(serverBatch, snapshot, offerResume)
  }

  async function acceptAuthoritativeBatch(
    serverBatch: CaptureBatch,
    snapshot: Pick<CaptureQueueSnapshot, "pendingPhotos">,
    offerResume: boolean,
  ): Promise<ReconciliationOutcome> {
    if (serverBatch.status !== "capturing") {
      batch.value = serverBatch
      resumable.value = null
      phase.value = "idle"
      return "continue"
    }

    let reconciled = serverBatch
    try {
      if (!isAwaitingFinalUploads(serverBatch)) {
        reconciled = await ensureCollectingGroup(serverBatch)
      }
    } catch (cause) {
      if (isAbortError(cause)) throw cause
      const fallback = localBatch.value ?? blockedQueueBatchFrom(serverBatch)
      blockCapture(
        { batch: fallback, pendingPhotos: snapshot.pendingPhotos },
        serverBatch,
        errorMessage(cause, "Reconnect to recover the incomplete item transition"),
        localBatch.value
          ? {
              kind: "reconcile",
              snapshot: { batch: localBatch.value, pendingPhotos: snapshot.pendingPhotos },
              offerResume,
            }
          : { kind: "initialize" },
      )
      return "handled"
    }

    const repaired = isAwaitingFinalUploads(reconciled)
      ? blockedQueueBatchFrom(reconciled)
      : queueBatchFrom(reconciled)
    const priorTransition = localBatch.value?.transition
    localBatch.value = priorTransition ? { ...repaired, transition: priorTransition } : repaired
    batch.value = reconciled
    pendingPhotos.value = snapshot.pendingPhotos
    rememberPendingPhotos(snapshot.pendingPhotos)

    try {
      await saveBatch(options.accountId, repaired)
    } catch (cause) {
      if (isAbortError(cause)) throw cause
      error.value = errorMessage(cause, "Could not save the recovered capture on this device")
      resumable.value = null
      recoveryIntent.value = {
        kind: "persist_authoritative",
        serverBatch: reconciled,
        localBatch: repaired,
        pendingPhotos: snapshot.pendingPhotos,
        offerResume,
      }
      phase.value = "blocked"
      return "handled"
    }

    localBatch.value = repaired
    const capture = {
      batch: reconciled,
      snapshot: { batch: repaired, pendingPhotos: snapshot.pendingPhotos },
    }
    if (offerResume) {
      resumable.value = capture
      error.value = null
      recoveryIntent.value = null
      phase.value = "resume"
    } else {
      resumable.value = null
      restoreActiveCapture(capture)
      error.value = null
      recoveryIntent.value = null
      phase.value = "capturing"
    }
    return "handled"
  }

  async function ensureCollectingGroup(serverBatch: CaptureBatch): Promise<CaptureBatch> {
    const collecting = serverBatch.groups.find(
      (group) => group.status === "collecting" && !group.closed_at,
    )
    if (collecting) return serverBatch

    const nextGroup = await startNextCaptureGroup(serverBatch.id, abortController.signal)
    if (nextGroup.status !== "collecting" || nextGroup.closed_at) {
      throw new Error("Capture API did not return a collecting group")
    }
    return {
      ...serverBatch,
      unresolved_group_count: serverBatch.unresolved_group_count + 1,
      groups: serverBatch.groups.some((group) => group.id === nextGroup.id)
        ? serverBatch.groups.map((group) => (group.id === nextGroup.id ? nextGroup : group))
        : [...serverBatch.groups, nextGroup],
    }
  }

  async function handleNonResumableBatch(
    snapshot: CaptureQueueSnapshot,
    serverBatch: CaptureBatch | null,
    message?: string,
  ): Promise<ReconciliationOutcome> {
    if (snapshot.pendingPhotos.length > 0) {
      const retainedPhotosMessage =
        "This capture cannot be resumed. Its queued photos remain on this device."
      blockCapture(
        snapshot,
        serverBatch ?? offlineBatchFrom(snapshot.batch),
        message
          ? `${message}. ${retainedPhotosMessage}`
          : `This batch is already ${serverBatch!.status}. ${retainedPhotosMessage}`,
        { kind: "stale_queue", snapshot, serverBatch },
      )
      return "handled"
    }

    const finishing = snapshot.batch.transition?.kind === "finishing_batch"
    try {
      await clearFinishedBatch(options.accountId, snapshot.batch.batchId)
      batch.value = serverBatch
      localBatch.value = null
      pendingPhotos.value = []
      resumable.value = null
      error.value = message ?? null
      recoveryIntent.value = null
      phase.value = finishing && serverBatch ? "ready" : "idle"
      return finishing && serverBatch ? "handled" : "continue"
    } catch (cause) {
      blockCapture(
        snapshot,
        serverBatch ?? offlineBatchFrom(snapshot.batch),
        errorMessage(cause, "Could not clear the stale saved capture"),
        {
          kind: "stale_cleanup",
          snapshot,
          serverBatch,
          target: finishing && serverBatch ? "ready" : "continue",
        },
      )
      return "handled"
    }
  }

  function blockCapture(
    snapshot: CaptureQueueSnapshot,
    authoritativeBatch: CaptureBatch,
    message: string,
    recovery: RecoveryIntent | null = null,
  ): void {
    batch.value = authoritativeBatch
    localBatch.value = snapshot.batch
    pendingPhotos.value = snapshot.pendingPhotos
    rejectedQueue.value = []
    rememberPendingPhotos(snapshot.pendingPhotos)
    resumable.value = null
    error.value = message
    recoveryIntent.value = recovery
    phase.value = "blocked"
  }

  function start(): Promise<void> {
    if (startPromise) return startPromise
    if (recoveryIntent.value) return Promise.resolve()
    phase.value = "starting"
    error.value = null
    queueBlocked.value = false

    startPromise = (async () => {
      try {
        const created = await createCaptureBatch(
          toValue(options.containerId),
          abortController.signal,
        )
        const queueState = queueBatchFrom(created)
        batch.value = created
        localBatch.value = queueState
        pendingPhotos.value = []
        rejectedQueue.value = []
        resumable.value = null
        try {
          await saveBatch(options.accountId, queueState)
        } catch (cause) {
          if (isAbortError(cause)) return
          error.value = errorMessage(cause, "Could not save the new capture on this device")
          recoveryIntent.value = {
            kind: "persist_created",
            serverBatch: created,
            localBatch: queueState,
          }
          phase.value = "blocked"
          return
        }
        recoveryIntent.value = null
        phase.value = "capturing"
      } catch (cause) {
        if (isAbortError(cause)) return
        error.value = errorMessage(cause, "Could not start bulk capture")
        phase.value = resumable.value ? "resume" : "idle"
      } finally {
        startPromise = null
      }
    })()
    return startPromise
  }

  function startNewBatch(): Promise<void> {
    if (startPromise) return startPromise
    return start()
  }

  function resume(): void {
    if (!resumable.value) return
    restoreActiveCapture(resumable.value)
    resumable.value = null
    phase.value = "capturing"
    if (pendingPhotos.value.length > 0) void resumeUploads()
  }

  function capturePhoto(blob: Blob): Promise<void> {
    if (!canCapture.value || !localBatch.value || !activeGroupId.value) {
      return Promise.resolve()
    }
    error.value = null

    const shutter: AcceptedShutter = {
      batch: localBatch.value,
      groupId: activeGroupId.value,
      capturedAt: new Date(),
      blob,
      sequence: nextShutterSequence++,
    }
    return persistAcceptedShutter(shutter).then(() => undefined)
  }

  function persistAcceptedShutter(shutter: AcceptedShutter): Promise<boolean> {
    const transaction = enqueueChain.then(async (): Promise<boolean> => {
      failedEnqueues.delete(shutter)
      try {
        const queued = await enqueuePhoto(options.accountId, shutter)
        rememberObjectUrl(queued)
        queuedPhotoSequences.set(queued.clientUploadId, shutter.sequence)
        pendingPhotos.value = pendingPhotos.value.some(
          (photo) => photo.clientUploadId === queued.clientUploadId,
        )
          ? pendingPhotos.value
          : insertQueuedPhotoInOrder(pendingPhotos.value, queued)
        failedEnqueues.delete(shutter)
        if (failedEnqueues.size === 0) queueBlocked.value = false
        if (
          failedEnqueues.size === 0 &&
          (phase.value === "capturing" || phase.value === "uploading")
        ) {
          void drainUploads(false)
        }
        return true
      } catch (cause) {
        failedEnqueues.add(shutter)
        if (cause instanceof CaptureQueueFullError) {
          queueBlocked.value = true
          error.value = "Capture storage is full. Existing photos are safe."
        } else {
          error.value = errorMessage(cause, "Could not save this photo")
        }
        const currentTarget =
          recoveryIntent.value?.kind === "enqueue_transition"
            ? recoveryIntent.value.target
            : "capture"
        recoveryIntent.value = {
          kind: "enqueue_transition",
          target: currentTarget,
          shutters: [...failedEnqueues],
        }
        phase.value = "blocked"
        return false
      } finally {
        inFlightEnqueues.delete(transaction)
        acceptedEnqueueCount.value = inFlightEnqueues.size
      }
    })
    enqueueChain = transaction.then(() => undefined)
    inFlightEnqueues.add(transaction)
    acceptedEnqueueCount.value = inFlightEnqueues.size
    return transaction
  }

  async function settleAcceptedEnqueues(target: "next" | "finish"): Promise<boolean> {
    const results = await Promise.all(inFlightEnqueues)
    if (results.every(Boolean) && failedEnqueues.size === 0) return true

    recoveryIntent.value = {
      kind: "enqueue_transition",
      target,
      shutters: [...failedEnqueues],
    }
    error.value =
      "A photo was accepted but could not be saved. Existing photos are safe; retry recovery before continuing."
    phase.value = "blocked"
    return false
  }

  function startNext(): Promise<void> {
    if (nextPromise) return nextPromise
    if (!canStartNext.value || !batch.value || !localBatch.value || !activeGroupId.value) {
      return Promise.resolve()
    }

    phase.value = "closing"
    error.value = null
    nextPromise = (async () => {
      let transitionPersisted = false
      let stableLocalBatch: CaptureQueueBatch | null = null
      try {
        if (
          (inFlightEnqueues.size > 0 || failedEnqueues.size > 0) &&
          !(await settleAcceptedEnqueues("next"))
        ) {
          return
        }
        if (!batch.value || !localBatch.value || !activeGroupId.value) {
          throw new Error("Capture state changed before the item could be closed")
        }
        const batchId = batch.value.id
        const groupId = activeGroupId.value
        const expectedPhotoCount = currentPhotoCount.value
        stableLocalBatch = localBatch.value
        localBatch.value = {
          ...localBatch.value!,
          transition: {
            kind: "closing_group",
            groupId,
            expectedPhotoCount,
          },
        }
        await saveBatch(options.accountId, localBatch.value)
        transitionPersisted = true

        const closed = await closeCaptureGroup(groupId, expectedPhotoCount, abortController.signal)
        replaceGroup(closed)
        localBatch.value = {
          ...withExpectedCount(localBatch.value!, groupId, expectedPhotoCount),
          transition: {
            kind: "awaiting_collecting_group",
            groupId,
            expectedPhotoCount,
          },
        }
        await saveBatch(options.accountId, localBatch.value)

        const nextGroup = await startNextCaptureGroup(batchId, abortController.signal)
        appendGroup(nextGroup)
        const repaired = withoutTransition({
          ...localBatch.value!,
          activeGroupId: nextGroup.id,
          groups: [
            ...localBatch.value!.groups.filter((group) => group.groupId !== nextGroup.id),
            {
              groupId: nextGroup.id,
              expectedPhotoCount: null,
              position: nextGroup.position,
              actualPhotoCount: nextGroup.actual_photo_count,
              acknowledgedClientUploadIds: nextGroup.photos.map((photo) => photo.client_upload_id),
            },
          ],
        })
        localBatch.value = { ...repaired, transition: localBatch.value!.transition }
        await saveBatch(options.accountId, repaired)
        localBatch.value = repaired
        recoveryIntent.value = null
        phase.value = "capturing"
      } catch (cause) {
        if (!isAbortError(cause)) {
          error.value = transitionPersisted
            ? errorMessage(
                cause,
                "The item transition is incomplete. Reconnect and retry; queued photos are safe.",
              )
            : errorMessage(cause, "Could not safely mark the next item transition")
        }
        if (!abortController.signal.aborted) {
          if (!transitionPersisted && stableLocalBatch) localBatch.value = stableLocalBatch
          recoveryIntent.value =
            transitionPersisted && localBatch.value
              ? {
                  kind: "reconcile",
                  snapshot: { batch: localBatch.value, pendingPhotos: durableSnapshotPhotos() },
                  offerResume: false,
                }
              : null
          phase.value = transitionPersisted ? "blocked" : "capturing"
        }
      } finally {
        nextPromise = null
      }
    })()
    return nextPromise
  }

  function retryRecovery(): Promise<void> {
    if (recoveryPromise) return recoveryPromise
    const intent = recoveryIntent.value
    if (!intent || phase.value !== "blocked") return Promise.resolve()

    phase.value = "recovering"
    error.value = null
    recoveryPromise = (async () => {
      try {
        switch (intent.kind) {
          case "initialize":
            await initialize()
            break
          case "reconcile": {
            const outcome = await reconcileLocalCapture(intent.snapshot, intent.offerResume)
            if (outcome === "continue") await initialize()
            break
          }
          case "persist_created":
            await revalidatePersistenceRecovery(intent.serverBatch.id, intent.localBatch, [], false)
            break
          case "persist_authoritative": {
            await revalidatePersistenceRecovery(
              intent.serverBatch.id,
              intent.localBatch,
              intent.pendingPhotos,
              intent.offerResume,
            )
            break
          }
          case "finish_cleanup":
            await clearFinishedBatch(options.accountId, intent.batchId)
            batch.value = intent.serverBatch
            localBatch.value = null
            pendingPhotos.value = []
            recoveryIntent.value = null
            phase.value = "ready"
            break
          case "refresh_finished":
            await refreshFinishedBatch(intent.batchId)
            break
          case "stale_cleanup":
            await clearFinishedBatch(options.accountId, intent.snapshot.batch.batchId)
            batch.value = intent.serverBatch
            localBatch.value = null
            pendingPhotos.value = []
            recoveryIntent.value = null
            if (intent.target === "ready") phase.value = "ready"
            else await initialize()
            break
          case "stale_queue":
            phase.value = "blocked"
            break
          case "enqueue_transition": {
            const results = await Promise.all(intent.shutters.map(persistAcceptedShutter))
            if (!results.every(Boolean)) {
              recoveryIntent.value = {
                ...intent,
                shutters: [...failedEnqueues],
              }
              phase.value = "blocked"
              return
            }
            recoveryIntent.value = null
            phase.value = "capturing"
            if (intent.target === "next") await startNext()
            else if (intent.target === "finish") await finish()
            else void drainUploads(false)
            break
          }
        }
      } catch (cause) {
        if (isAbortError(cause)) return
        recoveryIntent.value = intent
        error.value = errorMessage(cause, "Recovery failed. Try again.")
        phase.value = "blocked"
      }
    })().finally(() => {
      recoveryPromise = null
    })
    return recoveryPromise
  }

  async function revalidatePersistenceRecovery(
    batchId: string,
    priorLocalBatch: CaptureQueueBatch,
    priorPendingPhotos: QueuedCapturePhoto[],
    offerResume: boolean,
  ): Promise<void> {
    const current = await getCaptureBatch(batchId, abortController.signal)
    const collecting = current.groups.find(
      (group) => group.status === "collecting" && !group.closed_at,
    )
    if (current.status !== "capturing" || !collecting) {
      blockCapture(
        { batch: priorLocalBatch, pendingPhotos: priorPendingPhotos },
        current,
        current.status !== "capturing"
          ? `This batch is no longer capturing; it is ${current.status}.`
          : "This batch no longer has an active collecting item.",
      )
      return
    }

    localBatch.value = priorLocalBatch
    pendingPhotos.value = priorPendingPhotos
    await acceptAuthoritativeBatch(current, { pendingPhotos: priorPendingPhotos }, offerResume)
  }

  function replaceStaleQueue(): Promise<void> {
    if (staleActionPromise) return staleActionPromise
    const intent = recoveryIntent.value
    if (phase.value !== "blocked" || intent?.kind !== "stale_queue") {
      return Promise.resolve()
    }

    phase.value = "recovering"
    error.value = null
    let currentIntent = intent
    staleActionPromise = (async () => {
      try {
        const replacement =
          currentIntent.replacementBatch ??
          (await createCaptureBatch(
            currentIntent.snapshot.batch.containerId ?? null,
            abortController.signal,
          ))
        currentIntent = { ...currentIntent, replacementBatch: replacement }
        recoveryIntent.value = currentIntent
        const replacementQueue = queueBatchFrom(replacement)
        await replaceQueuedBatch(
          options.accountId,
          currentIntent.snapshot.batch.batchId,
          replacementQueue,
        )
        const remappedPhotos = currentIntent.snapshot.pendingPhotos.map((photo) => ({
          ...photo,
          batchId: replacementQueue.batchId,
          groupId: replacementQueue.activeGroupId,
        }))
        batch.value = replacement
        localBatch.value = replacementQueue
        pendingPhotos.value = remappedPhotos
        rejectedQueue.value = []
        recoveryIntent.value = null
        phase.value = "capturing"
        void resumeUploads()
      } catch (cause) {
        if (isAbortError(cause)) return
        recoveryIntent.value = currentIntent
        error.value = errorMessage(cause, "Could not preserve these photos in a new batch")
        phase.value = "blocked"
      }
    })().finally(() => {
      staleActionPromise = null
    })
    return staleActionPromise
  }

  function discardStaleQueue(): Promise<void> {
    if (staleActionPromise) return staleActionPromise
    const intent = recoveryIntent.value
    if (phase.value !== "blocked" || intent?.kind !== "stale_queue") {
      return Promise.resolve()
    }

    phase.value = "recovering"
    error.value = null
    staleActionPromise = (async () => {
      try {
        await clearFinishedBatch(options.accountId, intent.snapshot.batch.batchId)
        batch.value = null
        localBatch.value = null
        pendingPhotos.value = []
        rejectedQueue.value = []
        recoveryIntent.value = null
        await initialize()
      } catch (cause) {
        if (isAbortError(cause)) return
        recoveryIntent.value = intent
        error.value = errorMessage(cause, "Could not discard the saved photos")
        phase.value = "blocked"
      }
    })().finally(() => {
      staleActionPromise = null
    })
    return staleActionPromise
  }

  function finish(): Promise<void> {
    if (finishPromise) return finishPromise
    if (!canFinish.value || !batch.value || !activeGroupId.value) return Promise.resolve()

    phase.value = "finishing"
    error.value = null
    finishPromise = (async () => {
      let transitionPersisted = false
      let stableLocalBatch: CaptureQueueBatch | null = null
      try {
        if (
          (inFlightEnqueues.size > 0 || failedEnqueues.size > 0) &&
          !(await settleAcceptedEnqueues("finish"))
        ) {
          return
        }
        if (!batch.value || !localBatch.value || !activeGroupId.value) {
          throw new Error("Capture state changed before the batch could finish")
        }
        await drainUploads(false)
        if (rejectedQueue.value.length > 0) {
          const count = rejectedQueue.value.length
          const subject = count === 1 ? "photo was" : "photos were"
          const object = count === 1 ? "it" : "them"
          error.value = `${count} ${subject} rejected. Retry or discard ${object} before finishing.`
          phase.value = "capturing"
          return
        }
        if (pendingPhotos.value.length > 0) {
          const label = pendingPhotos.value.length === 1 ? "photo" : "photos"
          error.value = `${pendingPhotos.value.length} ${label} waiting to upload. Reconnect and try again.`
          phase.value = "capturing"
          return
        }

        const batchId = batch.value.id
        const expectedPhotoCount = currentPhotoCount.value
        stableLocalBatch = localBatch.value

        localBatch.value = {
          ...localBatch.value!,
          transition: {
            kind: "finishing_batch",
            groupId: activeGroupId.value!,
            expectedPhotoCount,
          },
        }
        try {
          await saveBatch(options.accountId, localBatch.value)
          transitionPersisted = true
        } catch (cause) {
          if (isAbortError(cause)) return
          localBatch.value = stableLocalBatch
          error.value = errorMessage(cause, "Could not safely mark this batch as finishing")
          phase.value = "capturing"
          return
        }

        const finished = await finishCaptureBatch(
          batchId,
          expectedPhotoCount,
          abortController.signal,
        )
        if (finished.status === "capturing") {
          throw new Error("Capture API did not finish the batch")
        }
        batch.value = finished
        try {
          await clearFinishedBatch(options.accountId, batchId)
        } catch (cause) {
          if (isAbortError(cause)) return
          error.value = errorMessage(cause, "Batch finished, but local cleanup failed")
          recoveryIntent.value = { kind: "finish_cleanup", serverBatch: finished, batchId }
          phase.value = "blocked"
          return
        }
        localBatch.value = null
        recoveryIntent.value = null
        phase.value = "ready"
      } catch (cause) {
        if (!isAbortError(cause)) {
          if (transitionPersisted && localBatch.value) {
            blockCapture(
              { batch: localBatch.value, pendingPhotos: durableSnapshotPhotos() },
              batch.value!,
              errorMessage(
                cause,
                "Finish status is uncertain. Reconnect to recover it; queued photos are safe.",
              ),
              {
                kind: "reconcile",
                snapshot: { batch: localBatch.value, pendingPhotos: durableSnapshotPhotos() },
                offerResume: false,
              },
            )
          } else {
            error.value = errorMessage(cause, "Could not finish this batch")
            phase.value = "capturing"
          }
        }
      } finally {
        finishPromise = null
      }
    })()
    return finishPromise
  }

  async function resumeUploads(): Promise<void> {
    if (!batch.value || pendingPhotos.value.length === 0) return
    const finishedBatchId = isAwaitingFinalUploads(batch.value) ? batch.value.id : null
    phase.value = "uploading"
    error.value = null
    const drained = await drainUploads(true)
    if (finishedBatchId && drained && pendingPhotos.value.length === 0) {
      await refreshFinishedBatch(finishedBatchId)
      return
    }
    if (!abortController.signal.aborted && phase.value === "uploading") phase.value = "capturing"
  }

  async function refreshFinishedBatch(batchId: string): Promise<void> {
    try {
      const refreshed = await getCaptureBatch(batchId, abortController.signal)
      batch.value = refreshed
      if (refreshed.status === "capturing") {
        throw new Error("The server is still waiting for the final capture uploads")
      }
      if (rejectedQueue.value.length > 0) {
        // The batch finished server-side but a final straggler was hard-rejected.
        // Retry is futile on a finished batch, but wiping it here would silently
        // drop the photo — surface it so the user can discard it deliberately.
        const count = rejectedQueue.value.length
        const subject = count === 1 ? "photo" : "photos"
        const object = count === 1 ? "it" : "them"
        error.value = `This batch finished, but ${count} ${subject} could not be added. Discard ${object} to clear this capture.`
        recoveryIntent.value = null
        phase.value = "capturing"
        return
      }
      await clearFinishedBatch(options.accountId, batchId)
      localBatch.value = null
      pendingPhotos.value = []
      recoveryIntent.value = null
      error.value = null
      phase.value = "ready"
    } catch (cause) {
      if (isAbortError(cause)) return
      recoveryIntent.value = { kind: "refresh_finished", batchId }
      error.value = errorMessage(cause, "Could not confirm the finished batch")
      phase.value = "blocked"
    }
  }

  function restoreActiveCapture(capture: ResumableCapture): void {
    batch.value = capture.batch
    localBatch.value = capture.snapshot.batch
    pendingPhotos.value = capture.snapshot.pendingPhotos
    rejectedQueue.value = []
    rememberPendingPhotos(capture.snapshot.pendingPhotos)
  }

  function rememberPendingPhotos(photos: QueuedCapturePhoto[]): void {
    for (const photo of photos) rememberObjectUrl(photo)
  }

  function drainUploads(showErrors: boolean): Promise<boolean> {
    if (uploadPromise) return uploadPromise
    uploadPromise = (async () => {
      while (pendingPhotos.value.length > 0) {
        const queued = pendingPhotos.value[0]
        try {
          const uploaded = await uploadCapturePhoto(queued, { signal: abortController.signal })
          await persistAcknowledgement(queued, uploaded)
          await acknowledgePhoto(options.accountId, queued.clientUploadId)
          acknowledgeInMemory(queued, uploaded)
          queueBlocked.value = false
        } catch (cause) {
          if (isAbortError(cause)) return false
          if (isHardRejection(cause)) {
            quarantinePhoto(queued, errorMessage(cause, "The server rejected this photo"))
            continue
          }
          if (showErrors) {
            error.value = errorMessage(cause, "Uploads are waiting for a connection")
          }
          return false
        }
      }
      return true
    })().finally(() => {
      uploadPromise = null
    })
    return uploadPromise
  }

  /**
   * Every durable photo still on this device: the upload queue plus the
   * quarantined rejects (both live in IndexedDB). Recovery snapshots must use
   * this, not bare `pendingPhotos`, or a quarantined photo gets orphaned in
   * IndexedDB and swept by a later clearFinishedBatch — silent loss.
   */
  function durableSnapshotPhotos(): QueuedCapturePhoto[] {
    if (rejectedQueue.value.length === 0) return pendingPhotos.value
    return rejectedQueue.value.reduce(
      (photos, entry) => insertQueuedPhotoInOrder(photos, entry.photo),
      pendingPhotos.value,
    )
  }

  /**
   * Move a hard-rejected photo out of the upload queue and into the rejected
   * list so a single poison photo can't block the rest of the batch. The photo
   * stays in IndexedDB — it only leaves on a confirmed discard or a successful
   * retry — so nothing is silently lost and it re-derives on reload.
   */
  function quarantinePhoto(queued: QueuedCapturePhoto, message: string): void {
    pendingPhotos.value = pendingPhotos.value.filter(
      (photo) => photo.clientUploadId !== queued.clientUploadId,
    )
    if (rejectedQueue.value.some((entry) => entry.photo.clientUploadId === queued.clientUploadId)) {
      return
    }
    rejectedQueue.value = [...rejectedQueue.value, { photo: queued, message }]
  }

  function retryRejectedPhoto(clientUploadId: string): Promise<void> {
    const rejected = rejectedQueue.value.find(
      (entry) => entry.photo.clientUploadId === clientUploadId,
    )
    if (!rejected) return Promise.resolve()
    rejectedQueue.value = rejectedQueue.value.filter(
      (entry) => entry.photo.clientUploadId !== clientUploadId,
    )
    pendingPhotos.value = pendingPhotos.value.some(
      (photo) => photo.clientUploadId === clientUploadId,
    )
      ? pendingPhotos.value
      : insertQueuedPhotoInOrder(pendingPhotos.value, rejected.photo)
    error.value = null
    return drainUploads(true).then(() => undefined)
  }

  async function discardRejectedPhoto(clientUploadId: string): Promise<void> {
    const rejected = rejectedQueue.value.find(
      (entry) => entry.photo.clientUploadId === clientUploadId,
    )
    if (!rejected) return
    try {
      await discardPhoto(options.accountId, clientUploadId)
    } catch (cause) {
      if (isAbortError(cause)) return
      error.value = errorMessage(cause, "Could not discard the rejected photo")
      return
    }
    rejectedQueue.value = rejectedQueue.value.filter(
      (entry) => entry.photo.clientUploadId !== clientUploadId,
    )
    purgeDiscardedFromRecovery(clientUploadId)
    releaseObjectUrl(clientUploadId)
    queuedPhotoSequences.delete(clientUploadId)
    error.value = null
  }

  /**
   * A discarded photo is gone from IndexedDB, but a recovery intent or resume
   * snapshot captured before the discard may still hold its blob in memory and
   * would re-queue and re-upload it on the next recovery. Scrub the id from
   * every in-memory snapshot so a confirmed discard is final.
   */
  function purgeDiscardedFromRecovery(clientUploadId: string): void {
    const without = (photos: QueuedCapturePhoto[]) =>
      photos.filter((photo) => photo.clientUploadId !== clientUploadId)

    const intent = recoveryIntent.value
    if (
      intent?.kind === "reconcile" ||
      intent?.kind === "stale_cleanup" ||
      intent?.kind === "stale_queue"
    ) {
      recoveryIntent.value = {
        ...intent,
        snapshot: { ...intent.snapshot, pendingPhotos: without(intent.snapshot.pendingPhotos) },
      }
    } else if (intent?.kind === "persist_authoritative") {
      recoveryIntent.value = { ...intent, pendingPhotos: without(intent.pendingPhotos) }
    }

    if (resumable.value) {
      resumable.value = {
        ...resumable.value,
        snapshot: {
          ...resumable.value.snapshot,
          pendingPhotos: without(resumable.value.snapshot.pendingPhotos),
        },
      }
    }
  }

  async function persistAcknowledgement(
    queued: QueuedCapturePhoto,
    uploaded: CapturePhoto,
  ): Promise<void> {
    if (!localBatch.value) return
    localBatch.value = {
      ...localBatch.value,
      groups: localBatch.value.groups.map((group) => {
        if (group.groupId !== queued.groupId) return group
        const acknowledged = new Set(group.acknowledgedClientUploadIds ?? [])
        acknowledged.add(uploaded.client_upload_id)
        return {
          ...group,
          actualPhotoCount: Math.max(group.actualPhotoCount ?? 0, acknowledged.size),
          acknowledgedClientUploadIds: [...acknowledged],
        }
      }),
    }
    await saveBatch(options.accountId, localBatch.value)
  }

  function acknowledgeInMemory(queued: QueuedCapturePhoto, uploaded: CapturePhoto): void {
    pendingPhotos.value = pendingPhotos.value.filter(
      (photo) => photo.clientUploadId !== queued.clientUploadId,
    )
    releaseObjectUrl(queued.clientUploadId)
    if (!batch.value) return
    const groups = batch.value.groups.map((group) => {
      if (group.id !== queued.groupId) return group
      if (group.photos.some((photo) => photo.id === uploaded.id)) return group
      return {
        ...group,
        actual_photo_count: group.actual_photo_count + 1,
        photos: [...group.photos, uploaded],
      }
    })
    batch.value = {
      ...batch.value,
      photo_count: groups.reduce((total, group) => total + group.actual_photo_count, 0),
      groups,
    }
  }

  function replaceGroup(replacement: CaptureGroup): void {
    if (!batch.value) return
    batch.value = {
      ...batch.value,
      groups: batch.value.groups.map((group) => {
        if (group.id !== replacement.id) return group
        const photos = [...replacement.photos]
        for (const existing of group.photos) {
          if (!photos.some((photo) => photo.id === existing.id)) photos.push(existing)
        }
        return {
          ...replacement,
          actual_photo_count: Math.max(
            replacement.actual_photo_count,
            group.actual_photo_count,
            photos.length,
          ),
          photos,
        }
      }),
    }
  }

  function appendGroup(group: CaptureGroup): void {
    if (!batch.value || batch.value.groups.some((entry) => entry.id === group.id)) return
    batch.value = { ...batch.value, groups: [...batch.value.groups, group] }
  }

  function rememberObjectUrl(photo: QueuedCapturePhoto): void {
    if (objectUrls.has(photo.clientUploadId) || !globalThis.URL?.createObjectURL) return
    objectUrls.set(photo.clientUploadId, URL.createObjectURL(photo.blob))
  }

  function insertQueuedPhotoInOrder(
    photos: QueuedCapturePhoto[],
    queued: QueuedCapturePhoto,
  ): QueuedCapturePhoto[] {
    // Every queued photo is sequenced either when accepted or when restored.
    const queuedSequence = queuedPhotoSequences.get(queued.clientUploadId)!
    const insertionIndex = photos.findIndex(
      (photo) => queuedPhotoSequences.get(photo.clientUploadId)! > queuedSequence,
    )

    if (insertionIndex === -1) return [...photos, queued]

    return [...photos.slice(0, insertionIndex), queued, ...photos.slice(insertionIndex)]
  }

  function releaseObjectUrl(clientUploadId: string): void {
    const url = objectUrls.get(clientUploadId)
    if (!url) return
    globalThis.URL?.revokeObjectURL?.(url)
    objectUrls.delete(clientUploadId)
  }

  onScopeDispose(() => {
    abortController.abort()
    for (const id of objectUrls.keys()) releaseObjectUrl(id)
  })

  return {
    phase: readonly(phase),
    batch: readonly(batch),
    pendingPhotos: readonly(pendingPhotos),
    rejectedPhotos,
    error: readonly(error),
    queueBlocked: readonly(queueBlocked),
    activeGroupId,
    currentPhotoCount,
    pendingCount,
    groupNumber,
    canCapture,
    canStartNext,
    canFinish,
    canRetryRecovery,
    canReplaceStaleQueue,
    canDiscardStaleQueue,
    currentThumbnails,
    suggestions,
    resumableBatch,
    resumableContainer,
    initialize,
    start,
    startNewBatch,
    resume,
    capturePhoto,
    retryRejectedPhoto,
    discardRejectedPhoto,
    startNext,
    retryRecovery,
    replaceStaleQueue,
    discardStaleQueue,
    finish,
    resumeUploads,
  }
}

function queueBatchFrom(batch: CaptureBatch): CaptureQueueBatch {
  let active: CaptureGroup | undefined
  for (let index = batch.groups.length - 1; index >= 0; index -= 1) {
    const group = batch.groups[index]
    if (group.status === "collecting" && !group.closed_at) {
      active = group
      break
    }
  }
  if (!active) throw new Error("Capture batch has no collecting group")
  return {
    ...queueBatchFields(batch),
    activeGroupId: active.id,
  }
}

function blockedQueueBatchFrom(batch: CaptureBatch): CaptureQueueBatch {
  const fallback = batch.groups[batch.groups.length - 1]
  if (!fallback) throw new Error("Capture batch has no groups")
  return {
    ...queueBatchFields(batch),
    activeGroupId: fallback.id,
  }
}

function queueBatchFields(batch: CaptureBatch): Omit<CaptureQueueBatch, "activeGroupId"> {
  return {
    batchId: batch.id,
    containerId: batch.container?.id ?? null,
    containerName: batch.container?.name ?? null,
    groups: batch.groups.map((group) => ({
      groupId: group.id,
      expectedPhotoCount: group.expected_photo_count,
      position: group.position,
      actualPhotoCount: group.actual_photo_count,
      acknowledgedClientUploadIds: group.photos.map((photo) => photo.client_upload_id),
    })),
  }
}

function withoutTransition(batch: CaptureQueueBatch): CaptureQueueBatch {
  const { transition: _transition, ...stable } = batch
  return stable
}

function offlineBatchFrom(local: CaptureQueueBatch): CaptureBatch {
  const now = new Date().toISOString()
  const groups = local.groups.map((group, index): CaptureGroup => ({
    type: "capture_group",
    id: group.groupId,
    position: group.position ?? index + 1,
    status: group.groupId === local.activeGroupId ? "collecting" : "pending",
    analysis_status: "not_queued",
    analysis_generation: 0,
    expected_photo_count: group.expectedPhotoCount,
    analysis_error: null,
    resolution_kind: null,
    actual_photo_count: group.actualPhotoCount ?? 0,
    analysis_data: {
      name: null,
      description: null,
      condition: null,
      quantity: null,
      model_number: null,
      serial_number: null,
      item_type_id: null,
      item_type_name: null,
      suggested_rotation: null,
    },
    closed_at: group.groupId === local.activeGroupId ? null : now,
    created_at: now,
    updated_at: now,
    resolved_item: null,
    photos: [],
  }))
  return {
    type: "capture_batch",
    id: local.batchId,
    name: "Saved capture",
    status: "capturing",
    container:
      local.containerId && local.containerName
        ? { id: local.containerId, name: local.containerName }
        : null,
    created_by: { id: "local", name: "You" },
    finished_at: null,
    completed_at: null,
    archived_at: null,
    created_at: now,
    updated_at: now,
    photo_count: groups.reduce((total, group) => total + group.actual_photo_count, 0),
    group_count: groups.length,
    unresolved_group_count: groups.length,
    groups,
  }
}

function withExpectedCount(
  batch: CaptureQueueBatch,
  groupId: string,
  expectedPhotoCount: number,
): CaptureQueueBatch {
  return {
    ...batch,
    groups: batch.groups.map((group) =>
      group.groupId === groupId ? { ...group, expectedPhotoCount } : group,
    ),
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError"
}

function isDefinitiveMissingBatch(error: unknown): error is CaptureApiError {
  return error instanceof CaptureApiError && [404, 410, 422].includes(error.status)
}

/**
 * Statuses that mean this photo can NEVER be stored: 422 (CaptureBatches::Error
 * / validation), 404 and 410 (the group or batch is gone — the upload endpoint
 * loads the group via `Current.account.capture_groups.find`, so a deleted group
 * 404s). Same permanent set the batch-verification path already trusts. Auth
 * (401/403), conflict (409), rate-limit (429), timeout (408), and 5xx are
 * transient — they must keep retrying on reconnect — so they are excluded, or a
 * mid-batch session expiry would quarantine every pending photo one by one.
 */
function isHardRejection(error: unknown): error is CaptureApiError {
  return error instanceof CaptureApiError && [404, 410, 422].includes(error.status)
}

function isAwaitingFinalUploads(batch: CaptureBatch): boolean {
  return batch.status === "capturing" && batch.finished_at !== null
}
